import {
	allocateMoneyTo,
	formatMoneyByDecimal,
	moneyParseByDecimal,
	TFormattedMoney,
	TMoney,
} from "@sembark-travel/money"
import { TVisaServiceInQuote } from "./store"
import { formatDate, isBefore } from "@sembark-travel/datetime-utils"
import { IDocType } from "../DocTypes"
import { IDocGroup } from "../DocGroups"

interface Pax {
	id: number
	type: "adult" | "child"
	accommodation?: "base" | "extra_bed" | "no_extra_bed" | "complimentary"
	room_sharing_type?: number
	price: TMoney[]
	hotels_price: TMoney[]
	cabs_price: TMoney[]
	travel_activities_price: TMoney[]
	flights_price: TMoney[]
	extras_price: TMoney[]
	visas_price: TMoney[]
	age?: number
}

interface ProcessVisaResult {
	error?: string
	used: number
	usedPaxIds: number[]
	allocations: {
		pax: Pax
		price: TMoney[]
	}[]
	allocatedPaxId: Record<number, boolean>
	paxSummary: {
		adult: number
		children: number
	}
}

type ProcessVisaServiceReturn =
	| null
	| {
			error: string
			used: 0
			allocations: []
			usedPaxIds: []
			allocatedPaxId: object
			paxSummary: { adult: 0; children: 0 }
	  }
	| ProcessVisaResult

function getPaxSummary(paxList: Pax[]) {
	let adult = 0
	let children = 0

	for (const p of paxList) {
		if (p.type === "adult") adult++
		else if (p.type === "child") children++
	}

	return { adult, children }
}

/** Pick up to `count` eligible pax for visa allocation */
function pickPaxForVisa({
	paxList,
	allocatedPaxId,
	count,
}: {
	paxList: Pax[]
	allocatedPaxId: Record<number, boolean>
	count: number
}): Pax[] {
	const picked: Pax[] = []

	for (const p of paxList) {
		if (allocatedPaxId[p.id]) continue
		picked.push(p)
		if (picked.length >= count) break
	}

	return picked
}

export function processVisasServiceForPax({
	service,
	pax,
	allocatedPaxId = {},
	onlyCountPax = false,
	visaCurrency,
}: {
	service?: Partial<TVisaServiceInQuote["services"][number]>
	pax: Pax[]
	allocatedPaxId?: Record<number, boolean>
	onlyCountPax?: boolean
	visaCurrency?: string
}): ProcessVisaServiceReturn {
	// Validate service has required fields
	if (!service) return null
	const givenPrice = service.given_price
	const paxCount = service.pax

	// Validate required fields
	if (givenPrice == null || paxCount == null || paxCount <= 0) {
		return {
			error: "Invalid visa service configuration: missing price or pax count",
			used: 0,
			allocations: [],
			usedPaxIds: [],
			allocatedPaxId: {},
			paxSummary: { adult: 0, children: 0 },
		}
	}

	// Filter out already allocated pax
	const availablePax = pax.filter((p) => !allocatedPaxId[p.id])
	const countrySuffix = service.country?.name
		? ` for ${service.country.name}`
		: ""

	if (availablePax.length === 0) {
		return {
			error: `No available pax for visa allocation for${countrySuffix}`,
			used: 0,
			allocations: [],
			usedPaxIds: [],
			allocatedPaxId: {},
			paxSummary: { adult: 0, children: 0 },
		}
	}

	// Check if requested pax count exceeds available pax
	if (paxCount > availablePax.length) {
		return {
			error: `Requested ${paxCount} pax but only ${availablePax.length} pax available for ${countrySuffix} (${availablePax.filter((p) => p.type === "adult").length} adults, ${availablePax.filter((p) => p.type === "child").length} children)`,
			used: 0,
			allocations: [],
			usedPaxIds: [],
			allocatedPaxId: {},
			paxSummary: { adult: 0, children: 0 },
		}
	}

	// Determine how many pax to allocate
	const used = paxCount // Now we know paxCount <= availablePax.length

	if (used <= 0) {
		return null
	}

	if (onlyCountPax) {
		const allocatedPax = pickPaxForVisa({
			paxList: availablePax,
			allocatedPaxId,
			count: used,
		})
		if (!allocatedPax.length) {
			return {
				error: "Failed to allocate pax",
				used: 0,
				allocations: [],
				usedPaxIds: [],
				allocatedPaxId: {},
				paxSummary: { adult: 0, children: 0 },
			}
		}

		return {
			paxSummary: getPaxSummary(allocatedPax),
			usedPaxIds: allocatedPax.map((p) => p.id),
			used: used,
			allocations: [],
			allocatedPaxId: {},
		}
	}

	// Money allocation for visa
	const totalMoney = moneyParseByDecimal(
		givenPrice, // Total price = price per pax * number of pax
		visaCurrency || service.visa?.currency || "INR" // Default to USD if currency not specified
	)
	const distributedPrices = allocateMoneyTo(totalMoney, used)

	const allocations: ProcessVisaResult["allocations"] = []
	const usedPaxIds: number[] = []
	const newAllocatedPaxId: Record<number, boolean> = {}

	let priceIndex = 0

	// Allocate to available pax
	for (const p of availablePax) {
		if (priceIndex >= distributedPrices.length) break

		const price = distributedPrices[priceIndex++]

		newAllocatedPaxId[p.id] = true
		usedPaxIds.push(p.id)

		allocations.push({
			pax: p,
			price: [price],
		})
	}

	return {
		allocations,
		usedPaxIds,
		paxSummary: getPaxSummary(allocations.map((a) => a.pax)),
		allocatedPaxId: newAllocatedPaxId,
		used: used,
	}
}

// Helper function to add visa price to pax
export function addVisaPriceToPax(pax: Pax, price: TMoney[]): Pax {
	return {
		...pax,
		visas_price: [...(pax.visas_price || []), ...price],
	}
}

export function isVisaRateExpired({
	rateValidityDate,
	startDate,
}: {
	rateValidityDate?: string
	startDate: string
}): boolean {
	if (!rateValidityDate || !startDate) {
		return false
	}
	return isBefore(
		formatDate(rateValidityDate, "DD MMM, YYYY"),
		formatDate(startDate, "DD MMM, YYYY"),
		"day"
	)
}

export function parseVisaRatePerPerson(
	rate?: number | string | TMoney | TFormattedMoney
): number {
	if (!rate) return 0
	if (typeof rate === "number") return rate
	if (typeof rate === "string") {
		const val = Number(rate)
		return Number.isNaN(val) ? 0 : val
	}
	if (typeof rate === "object" && rate !== null && "amount" in rate) {
		const r = rate as TMoney
		const val = Number(formatMoneyByDecimal(r))
		return Number.isNaN(val) ? 0 : val
	}
	return 0
}

export function getUniqueDocumentList(
	...documentGroupsList: (IDocGroup[] | undefined)[]
) {
	const documentMap = new Map<number, IDocType>()

	for (const groups of documentGroupsList) {
		if (!groups) continue
		for (const group of groups) {
			for (const doc of group?.document_types ?? []) {
				if (doc?.id && !documentMap.has(doc.id)) {
					documentMap.set(doc.id, doc)
				}
			}
		}
	}

	return Array.from(documentMap.values())
}

/**
 * Validates that visa services do not have combined pax exceeding the total trip pax for the same visa type.
 * @returns Map of field errors if any visa type has combined pax > totalPax
 */
export function validateVisaServicesPax(
	visas?: TVisaServiceInQuote,
	totalPax = 0
): { [key: string]: Array<string> } {
	const errors: { [key: string]: Array<string> } = {}
	if (!visas?.services?.length || totalPax <= 0) {
		return errors
	}

	const visaTypeMap = new Map<
		string | number,
		{
			name: string
			totalPaxForType: number
			serviceIndices: number[]
		}
	>()

	visas.services.forEach((service, index) => {
		if (!service.visa_type) return

		const visaTypeId =
			service.visa_type.id ??
			`${service.country?.id || service.country?.iso2 || ""}_${service.visa?.id || ""}_${service.visa_type.name || index}`

		const visaTypeName =
			service.visa_type.name ||
			(service.visa?.name ? `${service.visa.name} Visa` : "Visa")

		const countryLabel = service.country?.name
			? ` (${service.country.name})`
			: ""
		const displayName = `${visaTypeName}${countryLabel}`

		const pax = Number(service.pax) || 0

		const existing = visaTypeMap.get(visaTypeId)
		if (existing) {
			existing.totalPaxForType += pax
			existing.serviceIndices.push(index)
		} else {
			visaTypeMap.set(visaTypeId, {
				name: displayName,
				totalPaxForType: pax,
				serviceIndices: [index],
			})
		}
	})
	visaTypeMap.forEach(({ name, totalPaxForType, serviceIndices }) => {
		if (totalPaxForType > totalPax) {
			const errorMsg = `Combined pax (${totalPaxForType}) for visa type "${name}" cannot be more than total trip pax (${totalPax}).`

			serviceIndices.forEach((idx) => {
				const fieldKey = `visas.services.${idx}.pax`
				if (!errors[fieldKey]) {
					errors[fieldKey] = []
				}
				errors[fieldKey].push(errorMsg)
			})
		}
	})

	return errors
}
